import {FIELD_BIOME_LAYER_KEYS} from '../fieldBiomeEffects';
import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ЖИВАЯ ЛАВА — эффект подложки пекла (demon_1).
 *
 * Лава на картинке нарисована рекой и озерами вокруг поля, по краям стоят
 * действующие вулканы, а горы этой подложки сами облиты магмой. Эффект
 * оживляет все это (см. utils/effects/biome/livingLava):
 *  - ВОЛНА ЖАРА: по руслу раз за разом пробегает волна — нарисованная лава на
 *    пару секунд разгорается от оранжевого до желто-белого, участок за участком;
 *  - ВСПЛЕСКИ: на гребне волны лава выплевывает горсть сгустков магмы;
 *  - ВУЛКАНЫ: из кратеров идет темный дым, в жерлах дышит свет, и раз в
 *    несколько секунд вулкан выбрасывает залп сгустков — выше и гуще всплеска;
 *  - ГОРЫ: на магме, которой облиты жетоны гор, дышат огоньки — и только:
 *    всплески сгустков («искры») с жетонов гор владелец игры выключил.
 *
 * СГУСТОК МАГМЫ — ТЯЖЕЛЫЙ: это не искра и не пепел. Он вылетает рывком,
 * тормозится, на миг зависает в вершине и падает, разгоняясь, — летит как
 * брошенный камень, по параболе. В полете остывает: бело-желтый, оранжевый,
 * красный у вершины, а на пути вниз застывает в темно-серый камешек и только
 * потом тает. Ничего порхающего: ни покачивания, ни кувырка, ни мерцания.
 *
 * Жар на руслах и свет кратеров лежат на земле, под жетонами: это не подсветка
 * ячейки — пятно идет по нарисованному, а не по сетке. В середине поля лавы
 * нет: там живут только горы.
 *
 * А ПОД КРАЕМ ПОЛЯ ЛАВА ЕСТЬ: картинка вдвое больше игровой части, и почти на
 * каждом поле игры крайние ячейки ложатся на нарисованные русла — бывает, что
 * под рудник, флаг или гору. Такая лава светится (жар — под жетонами), но не
 * плюется: сгустки летят над жетонами и вылетали бы из-под строения, как из
 * него самого. Плюется только периферия: на самом поле сгустков нет вовсе.
 */
export const LIVING_LAVA_KIND = 'livingLava';

const LIVING_LAVA = Object.freeze({
  // Первая волна — почти сразу: с пустой, застывшей лавы партия не начинается
  firstDelayMs: {min: 200, max: 900},
  // Пауза между волнами (от начала предыдущей). Каждая волна идет по своему
  // руслу, так что разом горят три-пять русел из двух десятков
  intervalMs: {min: 550, max: 1100},
  // Больше стольких русел разом не горит
  maxGroups: 9,
  // Русло и кратер, которые видит камера, выбираются во столько раз охотнее
  // прочих: волн и залпов немного, и случаются они там, куда смотрит игрок.
  // Без камеры — все поровну
  inViewWeight: 4,
  // «Видит» — с запасом вокруг кадра, размеров ячейки: залп вулкана под нижним
  // краем кадра взлетает в кадр
  viewMarginFactor: 5,

  pulse: {
    // Сосед по руслу разгорается на столько позже предыдущего: волна бежит
    stepMs: 500,
    // Участок разгорается и гаснет одной плавной волной за столько. Медленно:
    // быстрая вспышка читалась бы ударом или подсветкой ячейки
    glowMs: 2400,
    // Яркость на гребне. Выше 0.5 лава выгорает в белые кляксы
    peakAlpha: 0.45,
    // Пятно чуть разбухает к гребню: в начале и в конце — эта доля размера
    swellFrom: 0.75,
  },

  // Пятно жара — мягкое светящееся пятно на земле (у текстуры яркая середина
  // занимает около трети кадра, поэтому кадр крупнее самого русла). Размер —
  // доля стороны картинки пейзажа, чтобы пятно не проглотило тонкую реку на
  // маленьком поле, в пределах стольких размеров ячейки
  glow: {
    // Тонкие русла
    river: {sideShare: 0.042, sizeFactor: {min: 2.2, max: 4.2}, tint: 0xffa040},
    // Широкая лава — озера, кратеры, разливы на излучинах: пятно крупнее и светлее
    wide: {sideShare: 0.062, sizeFactor: {min: 3.2, max: 6}, tint: 0xffd070},
    // Якорь с таким радиусом лавы (доля стороны картинки) и больше — широкий
    wideRadius: 0.004,
  },

  /**
   * СГУСТОК МАГМЫ — общее для всплесков русел и залпов вулканов. Сгусток —
   * пара частиц: плотная круглая капля (обычное наложение: на светящемся слое
   * темная краска не видна, а застывший сгусток темный) и мягкий ореол накала
   * вокруг нее (светится и гаснет, пока капля остывает).
   */
  clots: {
    // Тяготение одно на всех, размеров ячейки в секунду за секунду: оно и дает
    // ощущение веса. Высота взлета задает и время полета: залп вулкана в три
    // ячейки летит полторы секунды, всплеск в ячейку — меньше секунды. Меньше
    // пяти — сгустки плывут, как пепел
    gravityFactor: 8,
    // Сгусток падает не до места вылета: путь вниз — такая доля подъема.
    // Меньше трети — падение не успевает разогнаться, и вес не читается
    fallShare: {min: 0.45, max: 0.75},
    // Выброс — рывком: все сгустки вылетают за столько миллисекунд
    volleyMs: 160,
    // Крупный сгусток тяжелее: самый крупный берет высоту только из нижней
    // доли размаха высот своего выброса, самый мелкий — из всего размаха
    heavyRiseShare: 0.5,
    // Ветер матча клонит полет едва заметно: снос вбок — такая доля скорости
    // вылета. Сгусток — камень, а не пепел
    windLeanShare: 0.06,
    // На скорости капля вытянута вдоль полета и чуть сжата поперек (доли
    // размера на скорости вылета); в вершине, где скорость мала, — круглая
    stretch: {along: 0.55, across: 0.1},
    // Остывает: бело-желтый -> оранжевый (к этой доле подъема) -> красный
    // (в вершине) -> темно-серый камень (к этой доле падения)
    tints: {hot: 0xfff2bc, warm: 0xff9a3a, red: 0xd03a1a, solid: 0x3a3432},
    warmAt: 0.4,
    solidAt: 0.45,
    // Тает только застывший: с этой доли времени падения до его конца. Не
    // раньше solidAt: иначе сгусток исчезал бы еще красным. И не сразу следом:
    // путь вниз растет как квадрат времени, и до этой доли сгусток пролетает
    // только половину падения — темно-серый камешек успевает разогнаться на виду
    fadeFrom: 0.7,
    // Плотность капли
    alpha: 1,
    // Ореол накала: кадр мягкого пятна во столько раз крупнее капли (яркая
    // середина пятна — около трети кадра, то есть с каплю). Начинает гаснуть с
    // этой доли подъема и гаснет совсем к тому мигу, когда капля застыла;
    // остывая, сжимается до endScale
    halo: {sizeScale: 3.4, alpha: 0.9, coolFrom: 0.3, endScale: 0.6},
  },

  // ВСПЛЕСКИ РУСЕЛ на гребне волны жара: мелкие и низкие
  splashes: {
    // Слои: над жетонами строений и гор, но ПОД отрядами. Всплеск — быстрая
    // рыжая вспышка: над отрядом ее спутали бы с искрами попадания
    bodyLayer: FIELD_BIOME_LAYER_KEYS.LOW_AIR,
    haloLayer: FIELD_BIOME_LAYER_KEYS.LOW_AIR_GLOW,
    // С какой вероятностью участок русла плюнет и сколькими сгустками
    spitChance: 0.35,
    count: {min: 2, max: 4},
    // Жерла и лавовые озера (isVent у якоря) плюются всегда и гуще: потолок
    // ниже их не касается
    ventCount: {min: 4, max: 6},
    // Потолок сгустков для участков русел. Считает он не летящие сгустки, а
    // ЗАЯВЛЕННЫЕ живыми волнами: волна держит за собой все свои сгустки (и
    // участков, и жерл), пока жива, — до семи секунд, хотя летит каждый около
    // секунды; какие уже упали, построитель не знает. Пока заявлено столько,
    // новым участкам сгустков не достается (волна жара остается), а жерла и
    // озера плюются все равно — их сгустки заявляются сверх потолка (замер:
    // заявлено бывает до сорока). На деле в полете разом в среднем четыре
    // сгустка русел, изредка — до двух десятков с небольшим; участки
    // недополучают от шестой до пятой части своих сгустков
    maxLive: 28,
    // Вылетают не из одной точки: в пределах ширины лавы у якоря, но не уже
    // этой доли размера ячейки. По вертикали разброс вдвое меньше: озера на
    // картинке нарисованы сбоку и сплюснуты
    minSpreadFactor: 0.15,
    spreadFlatten: 0.5,
    // Кадр капли, размеров ячейки (сама капля — три четверти кадра). Вдвое
    // мельче прежних искр: это брызги, а не хлопья
    sizeFactor: {min: 0.1, max: 0.17},
    // Высота взлета, размеров ячейки
    riseFactor: {min: 0.7, max: 1.5},
    // Вылетают веером вокруг «вверх по экрану»: до стольких градусов в сторону
    fanDeg: 30,
    // Выше этой линии картинки (доля стороны сверху) всплескам нельзя: там
    // даль; якорю, которому до линии ближе этого, сгустков не достается
    ceilingV: 0.12,
    minRiseFactor: 1.2,
  },

  // ВУЛКАНЫ: дым, свет жерла и залпы сгустков. Сами кратеры — в списке подложки
  craters: {
    // Свет жерла: широкое красное зарево и светлое ядро поверх. Дышит неровно —
    // две волны с несовпадающими периодами; доля второй — secondMix. Вулканы
    // пекла извергаются вовсю: свет заметно ярче, чем у спящего кратера
    // вулканической подложки (там зарево до 0.4, ядро до 0.65). Ярче этого
    // нарисованный огонь выгорает в белое пятно
    glow: {
      // Один вдох-выдох жерла; вторая волна короче — такая доля этого срока
      breathMs: {min: 2200, max: 3000},
      secondPeriodShare: 0.37,
      secondMix: 0.3,
      // Размер кадра пятна — доля стороны картинки (у кратера с размером 1).
      // flatten — высота пятна в долях ширины: устье нарисовано сбоку, и
      // круглое ядро смотрелось бы в нем лампой
      wide: {sizeShare: 0.056, tint: 0xff4a1c, alpha: {min: 0.34, max: 0.62}, flatten: 0.9},
      core: {sizeShare: 0.026, tint: 0xff8a3c, alpha: {min: 0.45, max: 0.78}, flatten: 0.7},
      // На вдохе пятно чуть меньше: эта доля размера
      swellFrom: 0.85,
    },

    // Дым: клубы один за другим поднимаются от огненной струи вверх по экрану
    // (картинка нарисована сбоку), разбухая и тая. Лежит на земле, обычным
    // наложением: затемнение края картинки гасит его, как нарисованный дым
    smoke: {
      // Первый клуб — сразу
      firstDelayMs: {min: 0, max: 200},
      // Клуб живет около восьми секунд; у четырех кратеров разом около 24 клубов
      intervalMs: {min: 240, max: 380},
      maxGroups: 26,
      // Дым не начинается с пустого места: столько клубов рождаются уже в пути
      prewarm: 20,
      lifeMs: {min: 6500, max: 9500},
      // Путь вверх: 1 - (1 - t)^power — быстро из жерла, дальше все медленнее
      progressPower: 1.35,
      // Размер кадра клуба у жерла и в конце пути, доли стороны (у кратера с
      // размером 1): дым расходится
      sizeShare: {from: 0.022, to: 0.058},
      // Клубы разного размера (доля от расчетного)
      sizeJitter: {min: 0.85, max: 1.15},
      // Разброс клубов вокруг оси столба у жерла и в конце пути, доли стороны
      scatterShare: {from: 0.002, to: 0.011},
      // Ветер матча клонит столб слегка: наверху снос вбок — такая доля его высоты
      windLeanShare: 0.22,
      // Клуб медленно кружится, в любую сторону
      spinDegPerSecond: 8,
      // У жерла дым почти черный, с отсветом огня; выше — серый (доли срока,
      // за которые меняется краска)
      tints: {young: 0x3a2e2e, old: 0x5a5556, turnFrom: 0.1, turnTo: 0.55},
      // Плотность: проступает за fadeInShare срока и тает как (1 - t)^fadePower
      alpha: 0.42,
      fadeInShare: 0.2,
      fadePower: 1.3,
    },

    // Залпы: вулкан выбрасывает горсть сгустков — выше и гуще всплеска русла
    eruptions: {
      // Первый залп — в первую же секунду: вулканы пекла не спят
      firstDelayMs: {min: 300, max: 1200},
      // Пауза между залпами разных кратеров; один кратер дважды разом не бьет
      intervalMs: {min: 500, max: 1100},
      maxGroups: 3,
      // После залпа кратер молчит еще столько: бьет неровно, раз в две-пять секунд
      restMs: {min: 300, max: 2400},
      // Слои: кратеры стоят далеко за полем, залп — часть нарисованного вулкана
      // и лежит на земле: край картинки гасит его, как нарисованный огонь
      bodyLayer: FIELD_BIOME_LAYER_KEYS.GROUND,
      haloLayer: FIELD_BIOME_LAYER_KEYS.GROUND_GLOW,
      // Сгустков в залпе: больше, чем плюет лавовое озеро
      count: {min: 6, max: 9},
      // Кадр капли и высота взлета, размеров ячейки (у кратера с размером 1)
      sizeFactor: {min: 0.12, max: 0.22},
      riseFactor: {min: 2.2, max: 3.8},
      // Жерло бьет вверх: веер уже, чем у всплеска
      fanDeg: 20,
      // Сгустки вылетают из жерла: полуширина его устья, доля стороны картинки
      // (у кратера с размером 1)
      mouthShare: 0.004,
      // Вспышка жерла на залпе: разгорается рывком и гаснет. Размер кадра —
      // доля стороны (у кратера с размером 1); сплюснута, как само устье
      flare: {riseMs: 70, decayMs: 650, sizeShare: 0.044, tint: 0xffb060, alpha: 0.4, startScale: 0.6, flatten: 0.75},
    },
  },

  // ГОРЫ: жетоны гор пекла облиты магмой — она дышит. Места магмы на жетоне —
  // в списке подложки
  mountains: {
    // Гора, которую видит камера (с запасом в столько размеров ячейки),
    // выбирается во столько раз охотнее прочих
    inViewWeight: 8,
    viewMarginFactor: 1,

    // Огоньки на магме: гора «просыпается» — несколько ее огоньков плавно
    // проступают, дышат и гаснут. Слой — над жетоном горы, под отрядами
    glow: {
      layer: FIELD_BIOME_LAYER_KEYS.LOW_AIR_GLOW,
      // Горы просыпаются одна за другой: пауза между ними
      firstDelayMs: {min: 0, max: 400},
      intervalMs: {min: 350, max: 800},
      // Разом дышит не больше стольких гор
      maxGroups: 10,
      // Партия не начинается с темных гор
      prewarm: 6,
      // Сколько мест магмы светится у одной горы
      spotCount: {min: 3, max: 4},
      // Гора дышит столько — два-три вдоха — и засыпает
      lifeMs: {min: 9000, max: 14000},
      // Дыхание медленное: один вдох-выдох за столько. Быстрее двух секунд —
      // уже мигающие лампочки
      breathMs: {min: 3200, max: 4800},
      // На выдохе огонек не гаснет совсем: эта доля яркости
      breathFloor: 0.6,
      // Огоньки одной горы дышат чуть вразнобой: сдвиг фазы — до стольких долей круга
      spreadShare: 0.18,
      // Проступает и гаснет плавно (доли срока)
      fadeInShare: 0.18,
      fadeOutShare: 0.22,
      /**
       * СВЕТ ОГОНЬКА — два мягких пятна друг на друге (слой светится: цвета
       * складываются): жаркая СЕРДЦЕВИНА на самой трещине и широкий ОРЕОЛ
       * вокруг нее. Владелец игры дважды просил свет на жетонах гор ярче —
       * во второй раз «раза в два ярче и больше»: искры с гор он выключил, и
       * магма жетонов должна гореть сама. Одному пятну ярче полной краски не
       * стать (прежнее — 0.95), поэтому свет удвоен вторым пятном: в середине
       * огонька краски сердцевины и ореола складываются (вместе — 1.6 против
       * прежних 0.95, и сердцевина светлее и желтее), а ореол вдвое шире
       * прежнего пятна и заливает рыжим отсветом камень вокруг трещины.
       *
       * sizeFactor — кадр пятна, размеров ячейки на единицу размера места: у
       * сердцевины яркая середина пятна — с ширину нарисованной трещины; ореол
       * у самого крупного места — в две трети ширины ячейки (потолок слоя —
       * три четверти). tint и alpha — краска и яркость на вдохе
       */
      core: {sizeFactor: 0.62, tint: 0xffa850, alpha: 1},
      halo: {sizeFactor: 1.1, tint: 0xff7a28, alpha: 0.6},
      // На выдохе пятно чуть меньше: эта доля размера
      swellFrom: 0.85,
    },
  },
});

/**
 * Лава на картинке demon_1: 70 якорей в 21 русле (A..U), сверены с картинкой.
 * Якорь — точка на нарисованной лаве: u, v — доли стороны картинки от левого
 * верхнего угла, radius — полуширина лавы в этом месте (тоже доля стороны).
 * Порядок якорей — вдоль нарисованного русла: в нем и бежит волна жара.
 * isVent — жерло вулкана или лавовое озеро.
 */
const DEMON_1_CHAINS = Object.freeze([
  // A — лавовое поле на юго-западе: река от края поля к большому озеру и дальше
  {id: 'A', anchors: [
    {u: 0.385, v: 0.688, radius: 0.0031},
    {u: 0.36, v: 0.716, radius: 0.002},
    {u: 0.33, v: 0.727, radius: 0.0028},
    {u: 0.307, v: 0.756, radius: 0.0088, isVent: true},
    {u: 0.309, v: 0.784, radius: 0.004},
    {u: 0.262, v: 0.768, radius: 0.0039},
    {u: 0.229, v: 0.783, radius: 0.0055},
    {u: 0.237, v: 0.82, radius: 0.0059},
    {u: 0.217, v: 0.82, radius: 0.0035},
    {u: 0.189, v: 0.848, radius: 0.002},
  ]},
  // B — западная река, нижняя часть, с боковым рукавом
  {id: 'B', anchors: [
    {u: 0.191, v: 0.578, radius: 0.0031},
    {u: 0.209, v: 0.606, radius: 0.0049},
    {u: 0.209, v: 0.64, radius: 0.0028},
    {u: 0.172, v: 0.653, radius: 0.002},
    {u: 0.243, v: 0.606, radius: 0.002},
  ]},
  // C — западная река, верхняя часть
  {id: 'C', anchors: [
    {u: 0.124, v: 0.484, radius: 0.002},
    {u: 0.124, v: 0.512, radius: 0.0044},
    {u: 0.141, v: 0.536, radius: 0.0049},
    {u: 0.188, v: 0.546, radius: 0.002},
  ]},
  // D — большой вулкан на северо-востоке: огненная струя, кратер, поток по склону к углу поля
  {id: 'D', anchors: [
    {u: 0.838, v: 0.133, radius: 0.002},
    {u: 0.83, v: 0.156, radius: 0.0096, isVent: true},
    {u: 0.839, v: 0.179, radius: 0.002},
    {u: 0.798, v: 0.216, radius: 0.002},
    {u: 0.769, v: 0.251, radius: 0.0022},
    {u: 0.744, v: 0.277, radius: 0.002},
  ]},
  // E — круглое лавовое озеро на юго-западе
  {id: 'E', anchors: [
    {u: 0.249, v: 0.712, radius: 0.0098, isVent: true},
    {u: 0.221, v: 0.717, radius: 0.0049},
    {u: 0.235, v: 0.738, radius: 0.002},
  ]},
  // F — изогнутая восточная река; последние два якоря — уже в затухании у края
  {id: 'F', anchors: [
    {u: 0.781, v: 0.416, radius: 0.002},
    {u: 0.83, v: 0.423, radius: 0.002},
    {u: 0.814, v: 0.446, radius: 0.0044},
    {u: 0.834, v: 0.474, radius: 0.0031},
    {u: 0.885, v: 0.486, radius: 0.002},
    {u: 0.918, v: 0.484, radius: 0.002},
  ]},
  // G — юго-восточная река
  {id: 'G', anchors: [
    {u: 0.696, v: 0.666, radius: 0.002},
    {u: 0.731, v: 0.701, radius: 0.0035},
    {u: 0.768, v: 0.722, radius: 0.002},
  ]},
  // H — северо-западная река из кратерного озера
  {id: 'H', anchors: [
    {u: 0.304, v: 0.26, radius: 0.002},
    {u: 0.354, v: 0.29, radius: 0.0029},
    {u: 0.397, v: 0.295, radius: 0.002},
  ]},
  // I — северо-восточная река буквой S
  {id: 'I', anchors: [
    {u: 0.729, v: 0.328, radius: 0.0031},
    {u: 0.719, v: 0.362, radius: 0.002},
    {u: 0.696, v: 0.351, radius: 0.0031},
    {u: 0.672, v: 0.366, radius: 0.002},
  ]},
  // J — западный вулкан: струя, кратер, поток (у самого края картинки)
  {id: 'J', anchors: [
    {u: 0.104, v: 0.215, radius: 0.002},
    {u: 0.112, v: 0.244, radius: 0.0035, isVent: true},
    {u: 0.137, v: 0.271, radius: 0.002},
  ]},
  // K — северная река у верхнего края поля
  {id: 'K', anchors: [
    {u: 0.656, v: 0.228, radius: 0.002},
    {u: 0.614, v: 0.255, radius: 0.0039},
    {u: 0.579, v: 0.277, radius: 0.002},
  ]},
  // L — западная река буквой T
  {id: 'L', anchors: [
    {u: 0.175, v: 0.396, radius: 0.0028},
    {u: 0.166, v: 0.365, radius: 0.002},
    {u: 0.196, v: 0.371, radius: 0.0029},
    {u: 0.233, v: 0.375, radius: 0.002},
  ]},
  // M — кратерное лавовое озеро на северо-западе
  {id: 'M', anchors: [
    {u: 0.274, v: 0.292, radius: 0.0087, isVent: true},
  ]},
  // N — короткая восточная река
  {id: 'N', anchors: [
    {u: 0.795, v: 0.494, radius: 0.002},
    {u: 0.824, v: 0.535, radius: 0.002},
  ]},
  // O — вторая короткая восточная река
  {id: 'O', anchors: [
    {u: 0.816, v: 0.581, radius: 0.0035},
    {u: 0.815, v: 0.617, radius: 0.002},
  ]},
  // P — северо-западная река под западным вулканом
  {id: 'P', anchors: [
    {u: 0.217, v: 0.299, radius: 0.0035},
    {u: 0.24, v: 0.317, radius: 0.0028},
    {u: 0.265, v: 0.347, radius: 0.002},
  ]},
  // Q — два северных ручья
  {id: 'Q', anchors: [
    {u: 0.463, v: 0.188, radius: 0.002},
    {u: 0.466, v: 0.212, radius: 0.0028},
    {u: 0.453, v: 0.239, radius: 0.002},
  ]},
  // R — лавовое пятно на востоке
  {id: 'R', anchors: [
    {u: 0.774, v: 0.639, radius: 0.0035},
  ]},
  // S — жерло северного конуса
  {id: 'S', anchors: [
    {u: 0.588, v: 0.21, radius: 0.0028},
    {u: 0.575, v: 0.235, radius: 0.0022},
  ]},
  // T — кратер малого восточного вулкана (в затухании у края)
  {id: 'T', anchors: [
    {u: 0.887, v: 0.29, radius: 0.0028, isVent: true},
  ]},
  // U — маленькое озерцо на юго-западе
  {id: 'U', anchors: [
    {u: 0.252, v: 0.666, radius: 0.0031},
  ]},
]);

/**
 * Кратеры вулканов на картинке demon_1 — те, из которых на ней нарисованы
 * огненная струя и дым. Сверены с картинкой: вырезки вокруг каждого якоря с
 * признаком isVent и вокруг вулканов, увиденных на общем виде, с сеткой долей
 * стороны. Жерла D2, J2 и T1 — кратеры; A4, E1 и M1 — лавовые озера без дыма,
 * S — лавопад на скале: им дыма и залпов не положено.
 *
 *  - u, v — устье жерла (доли стороны картинки): здесь дышит свет и отсюда
 *    вылетают сгустки. anchor — якорь русла, которым этот кратер уже отмечен
 *    (устье лежит рядом с ним);
 *  - scale — размер вулкана относительно самого большого (D): на него
 *    умножаются свет, дым и залп;
 *  - smoke — нарисованный дым: от верха огненной струи (from) до места, где
 *    он расходится или уходит в затемнение края (to); density — сколько
 *    клубов достается кратеру относительно прочих;
 *  - volleyCeilingV — выше этой линии картинки сгусткам залпа нельзя; null —
 *    залпов нет.
 */
const DEMON_1_CRATERS = Object.freeze([
  // Большой вулкан на северо-востоке: струя до v 0.125, выше — черный столб дыма
  {
    id: 'D', anchor: 'D2', u: 0.83, v: 0.152, scale: 1,
    smoke: {from: {u: 0.833, v: 0.136}, to: {u: 0.843, v: 0.064}, density: 1},
    volleyCeilingV: 0.085,
  },
  // Западный вулкан у края картинки: дым бледнее и клонится влево
  {
    id: 'J', anchor: 'J2', u: 0.11, v: 0.236, scale: 0.85,
    smoke: {from: {u: 0.108, v: 0.222}, to: {u: 0.1, v: 0.152}, density: 1},
    volleyCeilingV: 0.165,
  },
  // Малый восточный вулкан: у самого затемнения края
  {
    id: 'T', anchor: 'T1', u: 0.884, v: 0.292, scale: 0.6,
    smoke: {from: {u: 0.885, v: 0.282}, to: {u: 0.892, v: 0.232}, density: 0.7},
    volleyCeilingV: 0.225,
  },
  // Дальний северный вулкан: якоря у него нет — он выше линии дали и целиком в
  // затемнении края. Ему — только слабый свет и короткий дым, без залпов
  {
    id: 'N', anchor: null, u: 0.716, v: 0.073, scale: 0.5,
    smoke: {from: {u: 0.717, v: 0.066}, to: {u: 0.726, v: 0.032}, density: 0.5},
    volleyCeilingV: null,
  },
]);

/**
 * Магма на жетоне горы пекла (тайл mountain_demon): места, где на картинке
 * жетона нарисованы светящиеся трещины и озерца. dx, dy — сдвиг от центра
 * ячейки в размерах ячейки (вправо и вниз), size — размер места относительно
 * обычной трещины.
 *
 * Сверены с картинкой жетона (830 на 630 точек). Игра рисует ее в масштабе
 * TOKEN_SCALE (0.23) серединой на 2 точки мира выше центра ячейки
 * (MOUNTAIN_SHIFT_Y в HexGridRenderer), размер ячейки — 60: точка картинки
 * (px, py) — это сдвиг ((px - 415) * 0.23, (py - 315) * 0.23 - 2) / 60.
 * Сменится картинка жетона или его масштаб — места надо сверить заново.
 */
const MOUNTAIN_DEMON_SPOTS = Object.freeze([
  // Трещина от вершины вниз по гребню (точка картинки 401, 150)
  {id: 'crest', dx: -0.054, dy: -0.666, size: 1},
  // Озерцо в середине жетона — самое яркое и широкое (398, 305)
  {id: 'pool', dx: -0.065, dy: -0.072, size: 1.25},
  // Трещина по восточному склону (528, 250)
  {id: 'east', dx: 0.433, dy: -0.283, size: 1},
  // Озерцо у западного края (252, 292)
  {id: 'west', dx: -0.625, dy: -0.122, size: 0.9},
  // Трещина от озерца к юго-западу (318, 368)
  {id: 'southWest', dx: -0.372, dy: 0.17, size: 0.9},
  // Озерцо у юго-восточного края (548, 462)
  {id: 'southEast', dx: 0.51, dy: 0.53, size: 1},
]);

export const LIVING_LAVA_EFFECT = Object.freeze({
  kind: LIVING_LAVA_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.DEMON_1]: Object.freeze({
      ...LIVING_LAVA, chains: DEMON_1_CHAINS, craterList: DEMON_1_CRATERS, mountainSpots: MOUNTAIN_DEMON_SPOTS,
    }),
  }),
});
